import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** When true the confirm button uses the danger styling (e.g. cancellations). */
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loading = false,
  destructive = false,
  onConfirm,
  onClose
}: Props) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box sx={{ p: 3, bgcolor: semanticColors.surface.canvas }}>
        <Typography
          sx={{
            ...typography.body.lBold,
            color: semanticColors.text.primary,
            mb: 1
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            ...typography.body.m,
            color: semanticColors.text.secondary,
            mb: 3
          }}
        >
          {message}
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box
            component="button"
            onClick={onClose}
            disabled={loading}
            sx={{
              flex: 1,
              height: "44px",
              borderRadius: "40px",
              border: `1.5px solid ${semanticColors.border.strong}`,
              bgcolor: "transparent",
              cursor: loading ? "not-allowed" : "pointer",
              ...typography.body.mBold,
              color: semanticColors.text.primary,
              "&:hover": { bgcolor: semanticColors.surface.containerLow }
            }}
          >
            {cancelLabel ?? t("common.no")}
          </Box>
          <Box
            component="button"
            onClick={onConfirm}
            disabled={loading}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              height: "44px",
              borderRadius: "40px",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              background: destructive
                ? semanticColors.status.error
                : semanticColors.gradient.surface,
              ...typography.body.mBold,
              color: semanticColors.text.inverse
            }}
          >
            {loading && (
              <CircularProgress
                size={14}
                sx={{ color: semanticColors.text.inverse }}
              />
            )}
            {confirmLabel ?? t("common.yes")}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
